import type { DocumentRecord } from '@/types/catalog'

// Doc types the backend partitions onto the P&ID side of a match.
const PID_SIDE_TYPES: ReadonlySet<DocumentRecord['type']> = new Set(['pnid', 'pfd', 'graph'])

// Match Entity pairs P&ID-side docs with timeseries docs. The action is
// offered only when the selection contains at least one of each side —
// the tenant endpoint can expand a single-sided selection against the
// tenant library, but the entry point must not advertise that.
export function canMatchEntitySelection(docs: ReadonlyArray<Pick<DocumentRecord, 'type'>>): boolean {
  let hasPidSide = false
  let hasTimeseries = false
  for (const doc of docs) {
    if (PID_SIDE_TYPES.has(doc.type)) hasPidSide = true
    else if (doc.type === 'timeseries') hasTimeseries = true
    if (hasPidSide && hasTimeseries) return true
  }
  return false
}

/**
 * Resolve the types of the selected docs.
 *
 * The catalog list is server-filtered and paginated, so a selected doc can be
 * absent from the rows currently loaded once a filter hides it (e.g. select a
 * timeseries, then filter to P&IDs). Resolving a selection's types from that
 * partial list under-counts and wrongly disables Match Entity. We prefer the
 * loaded record's type, then fall back to the type captured when the doc was
 * selected (it was on screen then). Ids with no resolvable type are dropped.
 */
export function resolveSelectedDocTypes(
  selectedIds: Iterable<string>,
  availableDocs: ReadonlyArray<Pick<DocumentRecord, 'id' | 'type'>>,
  capturedTypeById: Readonly<Record<string, DocumentRecord['type']>> = {},
): Array<Pick<DocumentRecord, 'type'>> {
  const typeById = new Map<string, DocumentRecord['type']>()
  for (const doc of availableDocs) typeById.set(doc.id, doc.type)

  const resolved: Array<Pick<DocumentRecord, 'type'>> = []
  for (const id of selectedIds) {
    const type = typeById.get(id) ?? capturedTypeById[id]
    if (type) resolved.push({ type })
  }
  return resolved
}
